import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { PHONE, PHONE_TEL } from "@/lib/api";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/lyon", label: "Lyon" },
  { to: "/drome", label: "Drôme" },
  { to: "/evenements", label: "Événements" },
  { to: "/blog", label: "Blog" },
  { to: "/devis", label: "Devis" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 border-b border-sand/80 bg-cream/75 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" data-testid="nav-logo" className="group flex items-baseline gap-2">
          <span className="font-serif text-2xl font-medium tracking-tight text-espresso">
            Gourmandise
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-terracotta transition-colors duration-300 group-hover:text-espresso">
            Traiteur
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-terracotta" : "text-espresso/70 hover:text-espresso"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={PHONE_TEL}
            data-testid="nav-phone-link"
            className="flex items-center gap-2 text-sm font-medium text-espresso/70 transition-colors duration-300 hover:text-terracotta"
          >
            <Phone size={14} strokeWidth={1.5} />
            {PHONE}
          </a>
          <Link to="/commander" data-testid="nav-order-cta" className="btn-primary !px-6 !py-3">
            Commander mon brunch
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="text-espresso lg:hidden"
          aria-label="Menu"
        >
          {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            data-testid="nav-mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-sand/80 bg-cream/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="py-3 font-serif text-2xl font-light text-espresso"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/commander"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-order-cta"
                className="btn-primary mt-4 w-full"
              >
                Commander mon brunch
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
